import React, { useState, useEffect, useCallback } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

const OptimizedImage = ({
  src,
  alt,
  className = '',
  placeholderSrc,
  threshold = 0.1,
  effect = 'blur',
  width,
  height,
  style = {},
  onClick,
  priority = false,
  fallbackSrc = '/images/placeholder.jpg',
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src);

  const handleError = useCallback(() => {
    if (!hasError) {
      setHasError(true);
      setCurrentSrc(fallbackSrc);
    }
  }, [hasError, fallbackSrc]);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  // For priority images, preload immediately
  useEffect(() => {
    if (priority && src) {
      const img = new Image();
      img.onload = handleLoad;
      img.onerror = handleError;
      img.src = src;

      return () => {
        img.onload = null;
        img.onerror = null;
      };
    }
  }, [src, priority, handleLoad, handleError]);

  if (hasError && !fallbackSrc) {
    return (
      <div 
        className={`bg-gray-200 flex items-center justify-center ${className}`}
        style={{ ...style, width, height }}
      >
        <span className="text-gray-500 text-sm">Image not available</span>
      </div>
    );
  }

  if (priority) {
    return (
      <img
        src={currentSrc}
        alt={alt}
        className={`${className} ${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
        style={{ ...style, width, height }}
        onClick={onClick}
        onError={handleError}
        onLoad={handleLoad}
        {...props}
      />
    );
  }

  return (
    <LazyLoadImage
      src={currentSrc}
      alt={alt}
      className={className}
      effect={effect}
      threshold={threshold}
      placeholderSrc={placeholderSrc}
      style={{ ...style, width, height }}
      onClick={onClick}
      onError={handleError}
      onLoad={handleLoad}
      {...props}
    />
  );
};

export default OptimizedImage;